import { useEffect, useRef, useState } from 'react';
import type { Item, Custo } from './types';
import { inputStyleSm } from './utils';
import ModalCadastrarCusto from './ModalCadastrarCusto';

// Combobox de descrição: campo único editável + botão "📚" que abre dropdown filtrável dos custos.
// Selecionar do catálogo preenche descrição/unidade/valor e mantém o vínculo (chip).
// Editar manualmente o texto não rompe o vínculo (a descrição pode divergir do nome no catálogo).
export default function DescricaoComboBox({ item, custos, onSelecionarCusto, onChangeDescricao, onUnlink, custoVinculado, onCustoCriado }: {
  item: Item; custos: Custo[];
  onSelecionarCusto: (c: Custo) => void;
  onChangeDescricao: (v: string) => void;
  onUnlink: () => void;
  custoVinculado: Custo | null;
  onCustoCriado?: (c: Custo) => void;  // notifica EditorComposicao pra adicionar à lista
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [modalCadastro, setModalCadastro] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Calcula posição do dropdown ao abrir e em scroll/resize (escapa do scroll do modal)
  useEffect(() => {
    if (!aberto) return;
    function reposicionar() {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const altura = 320; // máx do dropdown
      const espacoAbaixo = window.innerHeight - r.bottom;
      // Se não há espaço abaixo, abre acima
      const top = espacoAbaixo < altura && r.top > altura
        ? r.top - altura - 4
        : r.bottom + 4;
      setPos({ top, left: r.left, width: Math.max(r.width, 320) });
    }
    reposicionar();
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    return () => {
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
    };
  }, [aberto]);

  const filtrados = busca
    ? custos.filter(c => {
        const q = busca.toLowerCase();
        return c.descricao.toLowerCase().includes(q)
            || (c.codigo || '').toLowerCase().includes(q)
            || (c.tag || '').toLowerCase().includes(q);
      })
    : custos;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
        <input value={item.descricao}
          onChange={e => onChangeDescricao(e.target.value)}
          placeholder="Descrição livre ou clique 📚 para escolher do catálogo"
          style={{ ...inputStyleSm, flex: 1, fontSize: 11 }} />
        <button type="button" onClick={() => { setAberto(a => !a); setBusca(''); }}
          title="Vincular custo do catálogo"
          style={{
            padding: '4px 8px', fontSize: 12, borderRadius: 3,
            border: `0.5px solid ${custoVinculado ? '#0930D0' : '#ccc'}`,
            background: custoVinculado ? '#E7ECFD' : '#fff',
            cursor: 'pointer',
          }}>📚</button>
      </div>

      {custoVinculado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontSize: 10 }}>
          <span style={{
            background: '#E7ECFD', color: '#0930D0', padding: '1px 6px', borderRadius: 3,
            fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            🔗 {custoVinculado.tag ? `[${custoVinculado.tag}] ` : ''}{custoVinculado.descricao}
            <button onClick={onUnlink} type="button"
              style={{ background: 'none', border: 'none', color: '#0930D0', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}
              title="Remover vínculo">×</button>
          </span>
        </div>
      )}

      {aberto && pos && (
        <>
          <div onClick={() => setAberto(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 2001,
            background: '#fff', border: '0.5px solid #0930D0', borderRadius: 5,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            maxHeight: 320, display: 'flex', flexDirection: 'column',
          }}>
            <input autoFocus placeholder="Buscar por nome, código ou tag..."
              value={busca} onChange={e => setBusca(e.target.value)}
              style={{ ...inputStyleSm, margin: 6, fontSize: 11 }} />
            <div style={{ overflowY: 'auto', maxHeight: 220 }}>
              {filtrados.length === 0 && (
                <div style={{ padding: 16, fontSize: 11, color: '#666', textAlign: 'center' }}>
                  Nenhum custo encontrado{busca ? ` para "${busca}"` : ''}.
                </div>
              )}
              {filtrados.map(c => (
                <button key={c.id} type="button"
                  onClick={() => { onSelecionarCusto(c); setAberto(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '5px 10px', fontSize: 11, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    borderBottom: '0.5px solid #f0f0f0',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F0F1F5')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {c.tag && <span style={{ color: '#0930D0', fontWeight: 500 }}>[{c.tag}] </span>}
                  {c.descricao}
                  {c.composto && (
                    <span title={`Já soma: ${(c.componentes || []).map(p => p.descricao).join(', ')}. Não lance essas peças junto.`}
                      style={{ color: '#6B2FB3', marginLeft: 6, fontWeight: 500 }}>
                      · composto ({(c.componentes || []).length} peças)
                    </span>
                  )}
                  <span style={{ color: '#888', marginLeft: 6 }}>
                    — {c.unidade} · R$ {parseFloat(c.valor_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </button>
              ))}
            </div>
            {/* Rodapé com botão de cadastrar novo custo */}
            <div style={{ padding: 6, borderTop: '0.5px solid #eee', background: '#FAFBFF' }}>
              <button type="button"
                onClick={() => { setModalCadastro(true); setAberto(false); }}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 11, fontWeight: 600,
                  border: 'none', borderRadius: 4, background: '#0930D0', color: '#fff',
                  cursor: 'pointer',
                }}>
                + Cadastrar novo custo unitário{busca ? ` "${busca}"` : ''}
              </button>
            </div>
          </div>
        </>
      )}

      {modalCadastro && (
        <ModalCadastrarCusto
          descricaoInicial={busca}
          onClose={() => setModalCadastro(false)}
          onCriado={(novo) => {
            setModalCadastro(false);
            if (onCustoCriado) onCustoCriado(novo);
            onSelecionarCusto(novo);  // já vincula ao item atual
          }}
        />
      )}
    </div>
  );
}
